/* assets/faq-section.js */

class FAQSection {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = this.section.dataset.sectionId;
    this.tabTriggers = this.section.querySelectorAll(".faq-tab-trigger");
    this.tabContents = this.section.querySelectorAll(".faq-tab-content");
    this.accordionButtons = this.section.querySelectorAll(".faq-question");

    this.init();
  }

  init() {
    this.setupTabs();
    this.setupAccordions();
    this.setupAnimations();
    this.applyCustomStyles();
  }

  setupTabs() {
    this.tabTriggers.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        const tabId = trigger.dataset.tab;
        this.switchTab(tabId, trigger);
      });
    });
  }

  switchTab(activeTabId, activeTrigger) {
    // Remove active class from all triggers
    this.tabTriggers.forEach((trigger) => {
      trigger.classList.remove("active");
    });

    // Add active class to clicked trigger
    activeTrigger.classList.add("active");

    // Hide all tab contents
    this.tabContents.forEach((content) => {
      content.classList.remove("active");
    });

    // Show active tab content
    const activeContent = this.section.querySelector(
      `[data-content="${activeTabId}"]`
    );
    if (activeContent) {
      activeContent.classList.add("active");

      // Reset FAQ item animations
      this.animateFAQItems(activeContent);
    }
  }

  setupAccordions() {
    this.accordionButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.toggleAccordion(button);
      });
    });
  }

  toggleAccordion(button) {
    const answer = button.parentNode.querySelector(".faq-answer");
    const isOpen = button.classList.contains("active");

    // Close all other accordions in the same tab content
    const currentTabContent = button.closest(".faq-tab-content");
    const allButtonsInTab = currentTabContent.querySelectorAll(".faq-question");
    const allAnswersInTab = currentTabContent.querySelectorAll(".faq-answer");

    allButtonsInTab.forEach((btn) => btn.classList.remove("active"));
    allAnswersInTab.forEach((ans) => ans.classList.remove("active"));

    // Toggle current accordion
    if (!isOpen) {
      button.classList.add("active");
      answer.classList.add("active");
    }
  }

  setupAnimations() {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.animationPlayState = "running";
        }
      });
    }, observerOptions);

    // Observe animated elements
    const animatedElements = this.section.querySelectorAll(
      [".faq-header", ".faq-cta", ".faq-item-wrapper"].join(",")
    );

    animatedElements.forEach((el) => observer.observe(el));
  }

  animateFAQItems(container) {
    const faqItems = container.querySelectorAll(".faq-item-wrapper");

    faqItems.forEach((item, index) => {
      // Reset animation
      item.style.opacity = "0";
      item.style.transform =
        index % 2 === 0 ? "translateX(-50px)" : "translateX(50px)";

      // Trigger animation with delay
      setTimeout(() => {
        item.style.transition = "all 0.8s ease-out";
        item.style.opacity = "1";
        item.style.transform = "translateX(0)";
      }, index * 100);
    });
  }

  applyCustomStyles() {
    // Apply custom colors from Shopify settings
    const sectionSettings =
      window.shopifyThemeSettings?.[`section_${this.sectionId}`];

    if (sectionSettings) {
      const root = this.section;

      if (sectionSettings.background_color) {
        root.style.setProperty(
          "--faq-bg-color",
          sectionSettings.background_color
        );
      }

      if (sectionSettings.text_color) {
        root.style.setProperty("--faq-text-color", sectionSettings.text_color);
      }

      if (sectionSettings.accent_color) {
        root.style.setProperty(
          "--faq-accent-color",
          sectionSettings.accent_color
        );
      }
    }
  }

  // Smooth scroll to section
  scrollToSection() {
    this.section.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  // Public method to programmatically switch tabs
  switchToTab(tabId) {
    const targetTrigger = this.section.querySelector(`[data-tab="${tabId}"]`);
    if (targetTrigger) {
      this.switchTab(tabId, targetTrigger);
    }
  }

  // Public method to open specific FAQ item
  openFAQ(tabId, questionText) {
    this.switchToTab(tabId);

    setTimeout(() => {
      const buttons = this.section.querySelectorAll(".faq-question");
      const targetButton = Array.from(buttons).find(
        (button) =>
          button.querySelector(".question-text").textContent.trim() ===
          questionText
      );

      if (targetButton) {
        this.toggleAccordion(targetButton);
        targetButton.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 300);
  }

  // Destroy method for cleanup
  destroy() {
    this.tabTriggers.forEach((trigger) => {
      trigger.removeEventListener("click", this.switchTab);
    });

    this.accordionButtons.forEach((button) => {
      button.removeEventListener("click", this.toggleAccordion);
    });
  }
}

// Utility functions
const FAQUtils = {
  // Search FAQ items
  searchFAQ: function (searchTerm, sectionId) {
    const section = document.querySelector(`#faq-section-${sectionId}`);
    if (!section) return [];

    const results = [];
    const questions = section.querySelectorAll(".faq-question");
    const answers = section.querySelectorAll(".faq-answer-content");

    questions.forEach((question, index) => {
      const questionText = question.textContent.toLowerCase();
      const answerText = answers[index]?.textContent.toLowerCase() || "";
      const searchLower = searchTerm.toLowerCase();

      if (
        questionText.includes(searchLower) ||
        answerText.includes(searchLower)
      ) {
        const tabContent = question.closest(".faq-tab-content");
        const tabId = tabContent?.dataset.content;

        results.push({
          question: question.textContent.trim(),
          answer: answers[index]?.textContent.trim() || "",
          tabId: tabId,
          element: question,
        });
      }
    });

    return results;
  },

  // Highlight search results
  highlightSearchResults: function (results, searchTerm) {
    results.forEach((result) => {
      const questionEl = result.element.querySelector(".question-text");
      const answerEl = result.element.parentNode.querySelector(
        ".faq-answer-content"
      );

      if (questionEl) {
        questionEl.innerHTML = this.highlightText(
          questionEl.textContent,
          searchTerm
        );
      }

      if (answerEl) {
        answerEl.innerHTML = this.highlightText(
          answerEl.textContent,
          searchTerm
        );
      }
    });
  },

  // Helper function to highlight text
  highlightText: function (text, searchTerm) {
    const regex = new RegExp(`(${searchTerm})`, "gi");
    return text.replace(
      regex,
      '<mark style="background: yellow; color: black;">$1</mark>'
    );
  },

  // Remove highlights
  removeHighlights: function (sectionId) {
    const section = document.querySelector(`#faq-section-${sectionId}`);
    if (!section) return;

    const highlights = section.querySelectorAll("mark");
    highlights.forEach((mark) => {
      mark.outerHTML = mark.textContent;
    });
  },
};

// Initialize FAQ sections when DOM is loaded
document.addEventListener("DOMContentLoaded", function () {
  const faqSections = document.querySelectorAll('[id^="faq-section-"]');
  const faqInstances = new Map();

  faqSections.forEach((section) => {
    const instance = new FAQSection(section);
    const sectionId = section.dataset.sectionId;
    faqInstances.set(sectionId, instance);
  });

  // Make instances globally accessible
  window.FAQInstances = faqInstances;
  window.FAQUtils = FAQUtils;
});

// Handle theme editor updates
document.addEventListener("shopify:section:load", function (event) {
  const sectionContainer = event.target;
  const faqSection = sectionContainer.querySelector('[id^="faq-section-"]');

  if (faqSection) {
    const instance = new FAQSection(faqSection);
    const sectionId = faqSection.dataset.sectionId;

    if (window.FAQInstances) {
      window.FAQInstances.set(sectionId, instance);
    }
  }
});

document.addEventListener("shopify:section:unload", function (event) {
  const sectionContainer = event.target;
  const faqSection = sectionContainer.querySelector('[id^="faq-section-"]');

  if (faqSection && window.FAQInstances) {
    const sectionId = faqSection.dataset.sectionId;
    const instance = window.FAQInstances.get(sectionId);

    if (instance) {
      instance.destroy();
      window.FAQInstances.delete(sectionId);
    }
  }
});

// Export for use in other scripts
if (typeof module !== "undefined" && module.exports) {
  module.exports = { FAQSection, FAQUtils };
}
